// useDisasterChatsHooks.js

import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { pollDChat, postDChat, stopPollDChat } from 'slices/dchatSlice';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { parseError } from 'utils/parseTypedErrorMessage';
import {
  selectGroupGuid,
  selectIncident,
  useAppSelector,
} from '../../../slices/commonSelectors';
import { useCallback } from 'react';

export const keys = {
  base: 'disasterChats',
};

/**
 * List chats for the current workspace.
 *
 * @param {object} [scope]
 * @param {'self' | 'user' | 'workspace'} [scope.scope='self'] Whose chats: the
 *   caller's own (default), one teammate's, or the whole workspace's. Shared
 *   scopes need `incident:viewSharedChats`; rows carry owner_name/owner_email
 *   and chats owned by others open read-only. Default is `'self'`
 * @param {string | null} [scope.ownerGuid] Required for scope 'user'.
 * @param {boolean} [scope.archived=false] Self scope only: list the caller's
 *   archived chats instead of the live ones. Default is `false`
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] Default is `true`
 */
export const useDisasterChats = (
  { scope = 'self', ownerGuid = null, archived = false } = {},
  { enabled = true } = {}
) => {
  const incident = useAppSelector(selectIncident);
  const group_guid = useAppSelector(selectGroupGuid);

  return useQuery({
    queryKey: [keys.base, incident?.id, group_guid, scope, ownerGuid, archived],
    queryFn: async () => {
      // group_guid must be sent so group-scoped roles pass the accessAi permission check (and it is the
      // tenancy anchor for org-level workspace listings). Scope params are only sent when non-default so the
      // legacy self-scope call shape is unchanged.
      const response = await axios.get(`${config.apiGateway.disasterchat}/`, {
        params: {
          incident_id: incident?.id,
          group_guid,
          ...(scope !== 'self' && { scope }),
          ...(scope === 'user' && ownerGuid && { owner_guid: ownerGuid }),
          ...(archived && { archived: true }),
        },
      });
      return response.data;
    },
    enabled,
    staleTime: 0, // Ensure the query refetches immediately after invalidation
    throwOnError: () => {
      // Should fail silently
      return false;
    },
  });
};

export const useCreateDisasterChat = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const current_sitrep_id = useSelector(
    (state) => state.dchat.current_sitrep_id
  );
  const incident = useAppSelector(selectIncident);

  return useMutation({
    mutationFn: async (data) => {
      const payload = {
        ...data,
        current_sitrep_id,
        group_guid,
        incident,
      };
      return await axios.post(`${config.apiGateway.disasterchat}`, payload);
    },
    onMutate: () => {
      // Optional: Optimistically update the cache or UI here
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.base] });
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to create disaster chat', {
        autoClose: false,
      });
    },
  });
};

export const useUpdateDisasterChat = () => {
  const queryClient = useQueryClient();
  const group_guid = useSelector(selectGroupGuid);
  const current_sitrep_id = useSelector(
    (state) => state.dchat.current_sitrep_id
  );
  const incident = useAppSelector(selectIncident);

  return useMutation({
    mutationFn: async (data) => {
      const payload = {
        ...data,
        current_sitrep_id,
        group_guid,
        incident,
      };
      return await axios.put(`${config.apiGateway.disasterchat}`, payload);
    },
    onMutate: () => {
      // Optional: Optimistically update the cache or UI here
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.base] });
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to update disaster chat', {
        autoClose: false,
      });
    },
  });
};

/**
 * Archive a chat. The server's DELETE route archives rather than deletes
 * (PNG-537): the chat leaves every listing and can be restored from the owner's
 * Archived view.
 */
export const useArchiveDisasterChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    /** @param {string} dchat_id */
    mutationFn: async (dchat_id) => {
      return await axios.delete(`${config.apiGateway.disasterchat}`, {
        data: { dchat_id },
      });
    },
    onMutate: () => {
      // Optional: Optimistically update the cache or UI here
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.base] });
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to delete disaster chat', {
        autoClose: false,
      });
    },
  });
};

/** @deprecated Use useArchiveDisasterChat — "delete" has archive semantics. */
export const useDeleteDisasterChat = useArchiveDisasterChat;

/** Restore an archived chat (owner only). */
export const useRestoreDisasterChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    /** @param {string} dchat_id */
    mutationFn: async (dchat_id) => {
      return await axios.put(`${config.apiGateway.disasterchat}`, {
        dchat_id,
        archived: false,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.base] });
    },
    onError: (error) => {
      toast.error(error.message, { autoClose: false });
    },
  });
};

export const useCancelDisasterChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dchat) => {
      return await axios.post(`${config.apiGateway.disasterchat}/cancel`, {
        dchat_id: dchat.id,
        current_sitrep_id: dchat.current_sitrep_id,
      });
    },
    onMutate: () => {
      // Optional: Optimistically update the cache or UI here
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [keys.base] });
    },
    onError: (error) => {
      toast.error(parseError(error) ?? 'Failed to cancel disaster chat', {
        autoClose: false,
      });
    },
  });
};

export const usePollDChat = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useCallback(
    (dchatId) => {
      dispatch(pollDChat(dchatId));
      // Invalidate queries immediately after dispatch
      queryClient.invalidateQueries({ queryKey: [keys.base] });
    },
    [dispatch, queryClient]
  );
};

export const useStopPollDChat = () => {
  return useCallback(() => {
    stopPollDChat();
  }, []);
};

export const usePostDChat = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const incident = useAppSelector(selectIncident);

  return async (data) => {
    try {
      // Dispatch the Redux action
      await dispatch(postDChat({ ...data, incident }));

      // Invalidate the queries to refresh the chats list
      queryClient.invalidateQueries({ queryKey: [keys.base] });
    } catch (error) {
      toast.error(parseError(error) ?? 'Failed to send disaster chat message', {
        autoClose: false,
      });
    }
  };
};
